import {
  formatearFecha,
  type Pregunta,
  type Respuestas,
  VALORES_DE_ESCALA,
  validarRespuestas,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import {
  Aviso,
  Boton,
  BotonEnlace,
  CLASE_ENTRADA,
  EstadoVacio,
  Tarjeta,
} from "../componentes/ui.tsx";
import { mensajeDe } from "../utiles.ts";

export function PaginaResponderEncuesta() {
  const { id } = useParams({ from: "/app/encuestas/$id" });
  const trpc = useTRPC();
  const encuesta = useQuery(trpc.encuestas.paraResponder.queryOptions({ id }));
  const [respuestas, setRespuestas] = useState<Respuestas>({});
  const [error, setError] = useState<string | null>(null);
  const responder = useMutation(
    trpc.encuestas.responder.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries(),
      onError: (e) => setError(mensajeDe(e)),
    }),
  );

  if (encuesta.error) return <EstadoVacio>{mensajeDe(encuesta.error)}</EstadoVacio>;
  if (!encuesta.data) return <EstadoVacio>Cargando…</EstadoVacio>;
  const e = encuesta.data;

  const enlaceResultados = e.veResultados && (
    <BotonEnlace to="/encuestas/$id/resultados" params={{ id }}>
      Ver resultados
    </BotonEnlace>
  );

  function enviar() {
    if (!e) return;
    const validas = validarRespuestas(e.preguntas, respuestas);
    if ("problema" in validas) return setError(validas.problema);
    setError(null);
    responder.mutate({ id, respuestas: validas.respuestas });
  }

  return (
    <div className="mx-auto max-w-[640px]">
      <Encabezado
        overline={`Encuesta de ${e.autor}`}
        titulo={e.titulo}
        acciones={enlaceResultados}
      />
      {e.situacion === "respondida" && (
        <EstadoVacio>Gracias: tu respuesta ya quedó registrada.</EstadoVacio>
      )}
      {e.situacion === "cerrada" && <EstadoVacio>Esta encuesta ya cerró.</EstadoVacio>}
      {e.situacion === "no_aplica" && (
        <EstadoVacio>Esta encuesta no va dirigida a ti: no te toca responderla.</EstadoVacio>
      )}
      {e.situacion === "pendiente" && (
        <div className="space-y-4">
          {(e.descripcion || e.cierraEn) && (
            <p className="text-[13px] whitespace-pre-line text-texto-2">
              {e.descripcion}
              {e.cierraEn && (
                <span className="block text-texto-3">Cierra el {formatearFecha(e.cierraEn)}.</span>
              )}
            </p>
          )}
          <Aviso tono="ambar">
            {e.anonima
              ? "Esta encuesta es anónima: nadie podrá ver qué respondiste, solo que ya contestaste."
              : "Esta encuesta no es anónima: quien la creó verá tu nombre junto a tus respuestas."}
          </Aviso>
          {e.preguntas.map((p, i) => (
            <TarjetaDeRespuesta
              key={p.id}
              numero={i + 1}
              pregunta={p}
              valor={respuestas[p.id]}
              onCambio={(valor) => setRespuestas((r) => ({ ...r, [p.id]: valor }))}
            />
          ))}
          {error && <Aviso>{error}</Aviso>}
          <p className="text-[12px] text-texto-3">
            Una vez enviadas, tus respuestas ya no se pueden cambiar.
          </p>
          <div className="flex justify-end">
            <Boton variante="firma" disabled={responder.isPending} onClick={enviar}>
              {responder.isPending ? "Enviando…" : "Enviar respuestas"}
            </Boton>
          </div>
        </div>
      )}
    </div>
  );
}

const CLASE_OPCION =
  "flex cursor-pointer items-center gap-2.5 rounded-md border border-borde px-3 py-2.5 text-[13.5px] hover:bg-fondo has-[:checked]:border-marino has-[:checked]:bg-marino-claro";

function TarjetaDeRespuesta({
  numero,
  pregunta: p,
  valor,
  onCambio,
}: {
  numero: number;
  pregunta: Pregunta;
  valor: Respuestas[string] | undefined;
  onCambio: (valor: Respuestas[string]) => void;
}) {
  const elegidas = Array.isArray(valor) ? valor : [];
  const alternar = (opcion: string) =>
    onCambio(
      elegidas.includes(opcion) ? elegidas.filter((o) => o !== opcion) : [...elegidas, opcion],
    );

  return (
    <Tarjeta titulo={`${numero}. ${p.texto}`} {...(p.obligatoria ? {} : { etiqueta: "Opcional" })}>
      {p.tipo === "unica" && (
        <div className="space-y-1.5">
          {p.opciones.map((o) => (
            <label key={o} className={CLASE_OPCION}>
              <input type="radio" name={p.id} checked={valor === o} onChange={() => onCambio(o)} />
              {o}
            </label>
          ))}
        </div>
      )}
      {p.tipo === "multiple" && (
        <div className="space-y-1.5">
          <p className="text-[12px] text-texto-3">Puedes elegir varias.</p>
          {p.opciones.map((o) => (
            <label key={o} className={CLASE_OPCION}>
              <input type="checkbox" checked={elegidas.includes(o)} onChange={() => alternar(o)} />
              {o}
            </label>
          ))}
        </div>
      )}
      {p.tipo === "escala" && (
        <div>
          <div className="flex gap-2">
            {VALORES_DE_ESCALA.map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={valor === v}
                onClick={() => onCambio(v)}
                className={`h-11 flex-1 rounded-md border text-[15px] font-semibold ${
                  valor === v
                    ? "border-marino bg-marino text-white"
                    : "border-borde bg-white text-marino hover:bg-fondo"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <div className="mt-1 flex justify-between text-[11.5px] text-texto-3">
            <span>Muy bajo</span>
            <span>Muy alto</span>
          </div>
        </div>
      )}
      {p.tipo === "texto" && (
        <textarea
          value={typeof valor === "string" ? valor : ""}
          onChange={(e) => onCambio(e.target.value)}
          rows={3}
          aria-label={p.texto}
          className={CLASE_ENTRADA}
        />
      )}
    </Tarjeta>
  );
}
